import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatDate, formatTime } from '../../helpers/globalfunctions';

export function formatDistance(meters) {
    return (meters / 1000).toFixed(1) + ' km';
}

export function formatDuration(seconds) {
    return Math.round(seconds / 60) + ' min';
}

const ADDRESS_TRUNCATE_LENGTH = 100;

// Renders an address, clipped to ADDRESS_TRUNCATE_LENGTH chars with a clickable "..." that
// opens the full text in a popup when the address runs longer than that.
function renderAddress(address, title, onShowAddressPopup) {
    const text = address || '';
    if (text.length <= ADDRESS_TRUNCATE_LENGTH) {
        return <span title={text}>{text || '-'}</span>;
    }
    return (
        <span title={text}>
            {text.substring(0, ADDRESS_TRUNCATE_LENGTH)}
            <a href="#" className="cr-address-more" title="View full address" onClick={(e) => onShowAddressPopup(e, text, title)}>...</a>
        </span>
    );
}

// Font Awesome icon + colour modifier for a crew member's gender ("Male"/"M", "Female"/"F"); generic user icon otherwise.
function genderIcon(gender) {
    const g = (gender || '').trim().toLowerCase();
    if (g === 'male' || g === 'm') {
        return { className: 'fas fa-male', modifier: 'male' };
    }
    if (g === 'female' || g === 'f') {
        return { className: 'fas fa-female', modifier: 'female' };
    }
    return { className: 'fas fa-user', modifier: 'unknown' };
}

// "↓ Leg 1→2 · 33.9 km · 71 min" connector drawn above every card after the first.
function LegConnector({ index, leg }) {
    return (
        <div className="cr-leg">
            <span className="cr-leg-line"></span>
            <span className="cr-leg-pill">
                <i className="fas fa-arrow-down"></i> Leg {index}&rarr;{index + 1}
                <span className="cr-leg-sep">&middot;</span>{formatDistance(leg.distanceMeters)}
                <span className="cr-leg-sep">&middot;</span>{formatDuration(leg.durationSeconds)}
            </span>
            <span className="cr-leg-line"></span>
        </div>
    );
}

// One draggable reservation in the Club Reservations run order. useSortable is a hook,
// so this must be a function component. Styles live in ClubReservations.css (cr- prefix).
// Clicking the card (or Enter/Space on it) selects the passenger so their pickup → drop-off route
// is drawn on the map; the drag handle still reorders.
export function SortableReservationRow({ id, index, reservation, leg, onShowAddressPopup, isSelected, onSelect }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    const gender = genderIcon(reservation.gender);

    return (
        <div ref={setNodeRef} style={style}>
            {index > 0 && leg && <LegConnector index={index} leg={leg} />}
            <div
                className={`cr-card${isDragging ? ' cr-card-dragging' : ''}${isSelected ? ' cr-card-selected' : ''}`}
                role="button"
                tabIndex={0}
                aria-pressed={!!isSelected}
                title={isSelected ? 'Click to clear the highlight on the map' : 'Click to highlight this passenger on the map'}
                onClick={() => onSelect && onSelect(id)}
                onKeyDown={(e) => {
                    if (onSelect && (e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                        e.preventDefault();
                        onSelect(id);
                    }
                }}
            >
                <div className="cr-card-header">
                    <span className="cr-drag-handle" title="Drag to reorder" onClick={(e) => e.stopPropagation()} {...attributes} {...listeners}>
                        <i className="fas fa-grip-vertical"></i>
                    </span>
                    <span className="cr-order-badge">{index + 1}</span>
                    <div className="cr-trip">
                        <span className="cr-label">Trip #</span>
                        <span className="cr-trip-no">{reservation.tripNumber}</span>
                    </div>
                    <div className="cr-time">
                        <span className="cr-label">Pickup Time</span>
                        <span className="cr-time-chip" title="Pickup time">
                            <i className="far fa-clock"></i>
                            {formatDate(reservation.pickupDateTime, "MM/dd/yyyy")} {formatTime(reservation.pickupDateTime, "hh:mm")}
                        </span>
                    </div>
                </div>

                <div className="cr-card-body">
                    <div className="cr-passenger">
                        <span className={`cr-avatar cr-avatar-${gender.modifier}`} title={reservation.gender || 'Gender not set'}>
                            <i className={gender.className}></i>
                        </span>
                        <div className="cr-passenger-text">
                            <span className="cr-label">Passenger</span>
                            <div className="cr-passenger-name">
                                <span className="cr-name">{reservation.paxName}</span>
                                {reservation.gender && <span className={`cr-gender-pill cr-gender-${gender.modifier}`}>{reservation.gender}</span>}
                            </div>
                        </div>
                    </div>

                    <div className="cr-route">
                        <div className="cr-route-stop">
                            <span className="cr-route-marker cr-route-pickup"><i className="fas fa-circle"></i></span>
                            <div className="cr-route-text">
                                <span className="cr-label">Pickup</span>
                                <div className="cr-address">{renderAddress(reservation.puStreet, 'Pick-up Address', onShowAddressPopup)}</div>
                            </div>
                        </div>
                        <div className="cr-route-stop">
                            <span className="cr-route-marker cr-route-dropoff"><i className="fas fa-map-marker-alt"></i></span>
                            <div className="cr-route-text">
                                <span className="cr-label">Drop-off</span>
                                <div className="cr-address">{renderAddress(reservation.doStreet, 'Drop-off Address', onShowAddressPopup)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
